// Web (cloud-web) mirror of the shared network-feed recommendation logic
// (FTUE Network Feed Analysis spec, "Recommendation UI"; web read/apply task).
//
// The web FTUE / Settings "Tune your network" surface is a THIN view over the
// @neomaris/protocol recommendation shapes, emitting a `NetworkApplyDecisions`
// on confirm. The protocol package is not a dependency of the standalone
// cloud-web build (it ships only built `dist/` and is not linked into web's
// node_modules), so — exactly as web already mirrors the cloud `VesselDevice`
// response in useBoatDevices.ts — the small subset of protocol types this
// surface consumes is mirrored here. The protocol package and the cloud Zod
// schema (cloud/src/core/source-analysis/ingest.ts) remain the source of truth
// for the shapes; these types are kept structurally identical to them.

// ----- Mirrored @neomaris/protocol shapes (the subset web renders) ---------

export type GatewayRole = 'primary_io' | 'provides' | 'redundant' | 'standby' | 'disabled';

export interface GatewayRow {
  gatewayId: string;
  name: string;
  vendor?: string;
  model?: string;
  busGroupId: string | null;
  provides: { key: string; label: string }[];
  suggestedRole: GatewayRole;
  preferredIo: boolean;
  warnings: string[];
  /**
   * Present only on a gateway PARKED because it is a redundant twin of another
   * gateway on the SAME physical bus (task gateway_bus_dedupe) — e.g. "Same bus
   * as <primary>; parked to stop duplicate ingest." Rendered beside the
   * (unchanged) one-click re-enable control. Mirror of protocol `GatewayRow`.
   */
  parkedReason?: string;
  /**
   * True when this gateway can transmit NMEA 2000, so it may be the primary I/O
   * — including a gateway that is off and has never run (the first-run setup
   * parks every network gateway behind a local CAN interface, core ADR-170),
   * which the ranked lists cannot carry. Picking it is what turns it on.
   * Optional: absent from an older cloud. Mirror of protocol `GatewayRow`
   * (task dormant_gw_primary).
   */
  writable?: boolean;
}

/**
 * Per-source overlap facts (the subset the explain panel renders). Mirror of the
 * protocol `SourceOverlapFacts` — the deterministic floor the LLM reasons over
 * and the "Why?" expander shows verbatim, no LLM required. `uniqueKeysSample` is
 * a few representative normalized data-key identities only THIS source publishes
 * within its cluster (e.g. `pgn:127489@23`, `path:navigation.position`). `keys`
 * is the source's FULL normalized key set (sorted, capped ~64 by the cloud) so
 * the client can diff "secondary keys minus the chosen primary keys" pairwise as
 * the user changes the primary.
 */
export interface SourceOverlapFacts {
  sourceId: string;
  keyCount: number;
  redundantKeyCount: number;
  uniqueKeysSample: string[];
  keys: string[];
  // The deterministic ranking factors (healthy → fewest flaps → highest rate →
  // most devices). Present on the protocol wire shape + served by the cloud
  // (overlap.ts), but previously dropped by this mirror — needed so the client
  // can render `buildPrimaryReason` (the "why this is your main connection" line)
  // from the real factors, not just key counts. Health `state` is intentionally
  // NOT carried (it is not on the protocol facts shape); the reason cites
  // rate/flaps/devices only.
  msgRate: number;
  flapCount: number;
  deviceCount: number;
}

export interface SourceOverlapModel {
  requestId: string;
  generatedAt: string;
  clusters: { writableN2kRanked: string[]; sources: SourceOverlapFacts[] }[];
  unclustered: string[];
}

export interface SourceFeedRecommendation {
  requestId: string;
  sessionId: string;
  boatId: string;
  status: 'analyzing' | 'ready' | 'degraded' | 'failed';
  generatedAt: string;
  shape: 'clean' | 'single' | 'multi';
  overlap: SourceOverlapModel;
  rows: GatewayRow[];
  llm?: {
    preferredN2kGatewaySourceId: string | null;
    preferredGatewayReason: string;
    perSource: { sourceId: string; role: GatewayRole; sentence: string; confidence?: number }[];
  };
  accept: {
    disableSourceIds: string[];
    deprioritizeDataPaths: { sourceId: string; keys: string[] }[];
    preferredN2kInOutSourceId: string | null;
  };
  /** The requestId of the analysis this one was reevaluated from (set only on reevaluate). */
  reevaluatedFrom?: string;
  /** User pins that could not be honored (illegal/contradictory), with a plain-English reason. */
  constraintConflicts?: { sourceId: string; message: string }[];
}

export interface NetworkApplyDecisions {
  sessionId: string;
  roles: { gatewayId: string; role: GatewayRole }[];
  preferredN2kOutputSourceId: string | null;
  /**
   * Opt-in offers to CREATE before the plan's roles are applied (fixed wire
   * contract 4, bridge_ftue_offer / ADR-078) — each entry is a setup-session
   * `candidateId` for a secured `signalk_n2k_bridge` candidate the operator
   * opted into from the reveal's gateway beat (see
   * `lib/revealStaging.ts` `securedBridgeOffers`). Relayed VERBATIM by the
   * cloud apply route as `create: [{ candidateId }]`; absent on a normal apply
   * (the vast majority) so the boat creates nothing and raises no Signal K
   * access request. Only ever populated by {@link buildApplyDecisions}.
   */
  createCandidates?: string[];
}

/**
 * The user's edits to a recommendation, fed back into a reevaluate as HARD
 * constraints the AI must complete into a valid plan (not a finished plan to
 * apply raw). Each pinned role the AI honors when legal; an illegal pin (a
 * non-writable source pinned `primary_io`, or one that would leave a bus group
 * with no primary) is dropped to {@link SourceFeedRecommendation.constraintConflicts}
 * and the AI fills a legal choice — so the reevaluated result always passes the
 * web `validateDraft` gate. Mirror of the protocol `NetworkUserConstraints`.
 */
export interface NetworkUserConstraints {
  /** User-pinned roles to honor as hard constraints (illegal pins are dropped to conflicts). */
  roles: { sourceId: string; role: GatewayRole }[];
  /** User-pinned preferred N2K in/out gateway; null = no preference, undefined = unspecified. */
  preferredN2kOutputSourceId?: string | null;
}

// ----- Draft state + reducer ---------------------------------------------

/** The editable draft: one chosen role per gateway id (suggestion ⊕ choices). */
export interface RecommendationDraft {
  roles: Record<string, GatewayRole>;
}

export type RecommendationDraftAction =
  | { type: 'makePrimary'; gatewayId: string }
  | { type: 'toggleEnabled'; gatewayId: string; enabled: boolean }
  | { type: 'reset' };

/** Seed the draft from the recommendation's suggested roles. */
export function initialRecommendationDraft(rec: SourceFeedRecommendation): RecommendationDraft {
  const roles: Record<string, GatewayRole> = {};
  for (const row of rec.rows) roles[row.gatewayId] = row.suggestedRole;
  return { roles };
}

/** The effective draft role for a row (falls back to its suggested role). */
export function draftRoleFor(draft: RecommendationDraft, row: GatewayRow): GatewayRole {
  return draft.roles[row.gatewayId] ?? row.suggestedRole;
}

function rowById(rec: SourceFeedRecommendation, gatewayId: string): GatewayRow | undefined {
  return rec.rows.find((r) => r.gatewayId === gatewayId);
}

/**
 * Pure reducer over the draft.
 *  - `makePrimary`  — the primary-gateway radio: enforces exactly one Primary I/O
 *                     per bus group (demotes any prior primary in the SAME group
 *                     to `provides`) and is idempotent when the target is already
 *                     primary. Unclustered rows (null `busGroupId`) each form their
 *                     own singleton group and never demote each other.
 *  - `toggleEnabled`— the on/off switch. OFF parks the row (`redundant` for a
 *                     suggested-redundant twin, else `disabled`); ON derives an
 *                     "on" role from the suggestion and NEVER restores `primary_io`
 *                     (the radio is the only path to primary — restoring it would
 *                     create a second primary).
 * Returns the SAME reference on any no-op so React can skip re-renders.
 */
export function reduceRecommendationDraft(
  state: RecommendationDraft,
  action: RecommendationDraftAction,
  rec: SourceFeedRecommendation
): RecommendationDraft {
  switch (action.type) {
    case 'reset':
      return initialRecommendationDraft(rec);

    case 'makePrimary': {
      const target = rowById(rec, action.gatewayId);
      if (!target) return state;
      // Only N2K-writable gateways are eligible for Primary I/O.
      if (!allowedRoles(target, writableGatewayIds(rec)).includes('primary_io')) return state;
      // Idempotent: already this bus group's primary → no change (skip the
      // re-render, and never re-run the demotion sweep against ourselves).
      if (draftRoleFor(state, target) === 'primary_io') return state;
      const roles = { ...state.roles };
      // Exactly one primary per bus group: demote any other current primary in
      // the SAME busGroupId to `provides`. Unclustered rows (busGroupId null)
      // form ONE group for this purpose: "unclustered" means the cloud could not
      // prove they share a bus, not that they sit on different ones — a parked
      // gateway that has never run has no data to cluster on at all — and the
      // boat records a single preferred output gateway either way, so a second
      // "Primary" among them would be a badge the apply cannot honor
      // (task dormant_gw_primary).
      for (const row of rec.rows) {
        if (row.gatewayId === target.gatewayId) continue;
        if (row.busGroupId !== target.busGroupId) continue;
        const current = roles[row.gatewayId] ?? row.suggestedRole;
        if (current === 'primary_io') {
          roles[row.gatewayId] = 'provides';
        }
      }
      roles[target.gatewayId] = 'primary_io';
      return { roles };
    }

    case 'toggleEnabled': {
      const row = rowById(rec, action.gatewayId);
      if (!row) return state;
      const current = draftRoleFor(state, row);
      if (action.enabled) {
        // Turning a source ON. No-op if it is already feeding data. Otherwise
        // derive an "on" role from the suggestion — NEVER `primary_io`: the radio
        // is the only path to primary, and restoring it here would create a
        // second primary. A pure duplicate (suggested `redundant`) turned on
        // becomes a `standby` hot backup.
        if (roleIsActive(current)) return state;
        let next: GatewayRole;
        if (row.suggestedRole === 'provides' || row.suggestedRole === 'standby') {
          next = row.suggestedRole;
        } else if (row.suggestedRole === 'redundant') {
          next = 'standby';
        } else {
          // suggestedRole is `primary_io` or `disabled`.
          next = 'provides';
        }
        return { roles: { ...state.roles, [action.gatewayId]: next } };
      }
      // Turning a source OFF. No-op if it is already off. A suggested-redundant
      // twin parks as `redundant` (keeps the "Off (duplicate)" badge); anything
      // else parks as `disabled`. Both map to `redundant_disable` on apply.
      if (current === 'redundant' || current === 'disabled') return state;
      const off: GatewayRole = row.suggestedRole === 'redundant' ? 'redundant' : 'disabled';
      return { roles: { ...state.roles, [action.gatewayId]: off } };
    }

    default:
      return state;
  }
}

// ----- Eligibility + formatters ------------------------------------------

/** N2K-writable gateway ids — the union of every cluster's writableN2kRanked. */
export function writableGatewayIds(rec: SourceFeedRecommendation): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const cluster of rec.overlap.clusters) {
    for (const id of cluster.writableN2kRanked) ids.add(id);
  }
  // The ranked lists only cover gateways with OBSERVED data; a row the cloud
  // flags `writable` (a parked gateway that has never run, core ADR-170/171)
  // is writable by the boat's own word and stays in the primary picker.
  for (const row of rec.rows) {
    if (row.writable === true || row.preferredIo || row.suggestedRole === 'primary_io') {
      ids.add(row.gatewayId);
    }
  }
  return ids;
}

function isWritableRow(row: GatewayRow, writableIds?: ReadonlySet<string>): boolean {
  if (writableIds && writableIds.has(row.gatewayId)) return true;
  return row.writable === true || row.preferredIo || row.suggestedRole === 'primary_io';
}

/** Roles a row may legally take; only a writable gateway may be primary_io. */
export function allowedRoles(row: GatewayRow, writableIds?: ReadonlySet<string>): GatewayRole[] {
  const base: GatewayRole[] = ['provides', 'redundant', 'standby', 'disabled'];
  if (isWritableRow(row, writableIds)) return ['primary_io', ...base];
  return base;
}

/**
 * The badge label for a role. `redundant` is a duplicate kept OFF (fully covered
 * elsewhere); `standby` is the only backup — it stays ON, ready to take over. So
 * `redundant` reads "Off (duplicate)", never "backup".
 */
export function roleBadgeLabel(role: GatewayRole): string {
  switch (role) {
    case 'primary_io':
      return 'Primary';
    case 'provides':
      return 'Provides data';
    case 'redundant':
      return 'Off (duplicate)';
    case 'standby':
      return 'Standby backup';
    case 'disabled':
      return 'Off';
    default:
      return 'Provides data';
  }
}

/** Append the head noun " data" unless the label already ends in "data" — the
 *  coarse protocol chips ("NMEA 2000 data") already carry it, so a blind suffix
 *  produced "NMEA 2000 data data". */
function withDataNoun(label: string): string {
  return /\bdata$/i.test(label) ? label : `${label} data`;
}

/** "GPS, depth, and engine data" from the row's provides labels. */
export function providesSummary(row: GatewayRow): string {
  const labels = row.provides
    .map((p) => (typeof p.label === 'string' ? p.label.trim() : ''))
    .filter((l) => l.length > 0);
  if (labels.length === 0) return 'data';
  if (labels.length === 1) return withDataNoun(labels[0]!);
  if (labels.length === 2) return `${labels[0]} and ${withDataNoun(labels[1]!)}`;
  const head = labels.slice(0, -1).join(', ');
  const tail = labels[labels.length - 1]!;
  return `${head}, and ${withDataNoun(tail)}`;
}

// ----- Friendly gateway names (BUG: decision cards showed raw type slugs) -----

const HUMANIZE_VENDOR: Record<string, string> = {
  actisense: 'Actisense',
  maretron: 'Maretron',
  yacht_devices: 'Yacht Devices',
  digital_yacht: 'Digital Yacht',
  garmin: 'Garmin',
  raymarine: 'Raymarine',
  furuno: 'Furuno',
  victron: 'Victron',
  navico: 'Navico',
  signalk: 'Signal K',
};

const HUMANIZE_TYPE: Record<string, string> = {
  signalk: 'Signal K server',
  nmea2000_udp: 'NMEA 2000 gateway',
  nmea2000_tcp: 'NMEA 2000 gateway',
  nmea0183_udp: 'NMEA 0183 source',
  nmea0183_tcp: 'NMEA 0183 source',
  nmea0183_serial: 'NMEA 0183 source',
  actisense_w2k1: 'Actisense W2K-1',
  actisense_ngt1: 'Actisense NGT-1',
  maretron_ipg100: 'Maretron IPG100',
  maretron_usb100: 'Maretron USB100',
  yacht_devices_lan: 'Yacht Devices Network Gateway',
  yacht_devices_ydnu02: 'Yacht Devices YDNU-02',
  digital_yacht_ikonvert: 'Digital Yacht iKonvert',
  digital_yacht_navlink2: 'Digital Yacht NavLink2',
  digital_yacht_lanlink: 'Digital Yacht LANLink',
  // SocketCAN is the boat's own CAN interface, not a branded box — there is no
  // vendor to fall back on, so without this entry `humanizeSourceSlug` returns
  // the raw slug and the decision cards read `canboat_socketcan (can0)`.
  canboat_socketcan: 'NMEA 2000 bus',
  timezero_lan: 'TimeZero Professional',
  victron_venus_mqtt: 'Victron GX',
  starlink_dish: 'Starlink dish',
};

function humanizeSourceSlug(slug: string): string {
  const typed = HUMANIZE_TYPE[slug];
  if (typed !== undefined) return typed;
  const parts = slug.split('_');
  const vendor = HUMANIZE_VENDOR[parts[0] ?? ''];
  const model = parts.slice(1).join(' ').toUpperCase();
  if (vendor !== undefined && model.length > 0) return `${vendor} ${model}`;
  if (vendor !== undefined) return vendor;
  return slug;
}

/**
 * Humanize a source-analysis row name like `"maretron_ipg100 (10.3.0.130:6543)"`
 * into `"Maretron IPG100 (10.3.0.130:6543)"`, matching the friendly names the scan
 * beat shows (`candidateLabel`). Only the leading `sourceType` slug is rewritten;
 * any trailing `" (host:port)"` is preserved. The server row carries no vendor/model
 * (see `gatewayName`), so we humanize the slug rather than join by an unrelated id.
 */
export function humanizeGatewayName(name: string): string {
  const m = name.match(/^([a-z0-9_]+)(.*)$/i);
  if (m === null) return name;
  return `${humanizeSourceSlug(m[1] ?? '')}${m[2] ?? ''}`;
}

// ----- "Why these gateways?" explain selector ----------------------------

/** The structured justification the per-gateway "Why?" expander renders. */
export interface GatewayExplain {
  /** Human chips of what this gateway provides (from `GatewayRow.provides`). */
  provides: { key: string; label: string }[];
  /** Total distinct data keys this gateway publishes (`keyCount`), if known. */
  keyCount: number | null;
  /** How many of those keys another gateway on the same bus also publishes. */
  redundantKeyCount: number | null;
  /** How many keys ONLY this gateway publishes (keyCount − redundantKeyCount). */
  uniqueKeyCount: number | null;
  /** A few representative humanized identities ONLY this gateway contributes. */
  uniqueKeysSample: string[];
  /** True when this gateway sees data, but every key is seen elsewhere too. */
  fullyRedundant: boolean;
  /** One-line plain-English justification for the chosen/kept/dropped role. */
  summary: string;
  /**
   * Humanized data keys this gateway publishes that the CURRENT draft primary of
   * its bus group does NOT — i.e. what you'd lose by turning this one off. Empty
   * when everything it provides is also on the primary (justifies dropping it),
   * or when this row IS the primary / has no bus-group primary. Recomputed live
   * as the user changes the primary. Capped to bound the rendered list.
   */
  uniqueVsPrimary: string[];
  /** Display name of the current draft primary this row is diffed against, if any. */
  primaryName: string | null;
}

/** Cap on the rendered "what this secondary adds over the primary" list. */
const UNIQUE_VS_PRIMARY_LIMIT = 8;

/** Find the overlap facts for a gateway across every cluster (by sourceId). */
function overlapFactsFor(
  rec: SourceFeedRecommendation,
  gatewayId: string
): SourceOverlapFacts | undefined {
  for (const cluster of rec.overlap.clusters) {
    const facts = cluster.sources.find((s) => s.sourceId === gatewayId);
    if (facts) return facts;
  }
  return undefined;
}

/**
 * The CURRENT draft Primary I/O of this row's bus group, if any (and not this row
 * itself). Uses `draftRoleFor` so it tracks the user's `makePrimary` edits live;
 * unclustered rows (null `busGroupId`) have no group primary.
 */
function draftPrimaryRowFor(
  rec: SourceFeedRecommendation,
  row: GatewayRow,
  draft: RecommendationDraft
): GatewayRow | undefined {
  if (row.busGroupId == null) return undefined;
  return rec.rows.find(
    (r) =>
      r.gatewayId !== row.gatewayId &&
      r.busGroupId === row.busGroupId &&
      draftRoleFor(draft, r) === 'primary_io'
  );
}

/**
 * Humanize a normalized data-key identity string for display. The deterministic
 * model emits `pgn:<n>[@src]`, `path:<dotted.path>`, or `0183:<talker>:<sent>`;
 * we strip the machine prefix into a friendlier token. Anything unrecognized is
 * returned as-is (forward-compatible with new key kinds).
 */
export function humanizeDataKey(key: string): string {
  if (key.startsWith('pgn:')) return `PGN ${key.slice('pgn:'.length)}`;
  if (key.startsWith('path:')) return key.slice('path:'.length);
  if (key.startsWith('0183:')) return key.slice('0183:'.length).replace(':', ' ');
  return key;
}

/**
 * The per-gateway justification for the "Why these gateways?" explain panel
 * Pure: combines the row's `provides`
 * with the deterministic {@link SourceOverlapFacts} (`keyCount`,
 * `redundantKeyCount`, `uniqueKeysSample`) — no LLM, so it works in degraded
 * mode too. The `summary` reflects the gateway's CURRENT draft role so the
 * justification tracks edits live (an off duplicate vs. a kept standby read
 * differently). When a gateway joined no overlap cluster (no detected
 * redundancy) the facts are absent and everything it provides is unique.
 */
export function gatewayExplain(
  rec: SourceFeedRecommendation,
  row: GatewayRow,
  draft: RecommendationDraft
): GatewayExplain {
  const facts = overlapFactsFor(rec, row.gatewayId);
  const role = draftRoleFor(draft, row);
  const keyCount = facts ? facts.keyCount : null;
  const redundantKeyCount = facts ? facts.redundantKeyCount : null;
  const uniqueKeyCount =
    keyCount !== null && redundantKeyCount !== null ? keyCount - redundantKeyCount : null;
  const uniqueKeysSample = facts ? facts.uniqueKeysSample.map(humanizeDataKey) : [];
  const fullyRedundant =
    keyCount !== null && keyCount > 0 && uniqueKeyCount !== null && uniqueKeyCount === 0;

  // What THIS row adds over the current draft primary of its bus group: diff its
  // full key set minus the primary's, humanize, cap. Recomputes as the primary
  // changes (draft is threaded). Absent when there is no group primary to diff.
  const primaryRow = draftPrimaryRowFor(rec, row, draft);
  const primaryName = primaryRow ? (primaryRow.name.length > 0 ? primaryRow.name : null) : null;
  let uniqueVsPrimary: string[] = [];
  if (primaryRow && facts) {
    const primaryFacts = overlapFactsFor(rec, primaryRow.gatewayId);
    const primaryKeys = new Set(primaryFacts?.keys ?? []);
    uniqueVsPrimary = facts.keys
      .filter((k) => !primaryKeys.has(k))
      .slice(0, UNIQUE_VS_PRIMARY_LIMIT)
      .map(humanizeDataKey);
  }

  return {
    provides: row.provides,
    keyCount,
    redundantKeyCount,
    uniqueKeyCount,
    uniqueKeysSample,
    fullyRedundant,
    uniqueVsPrimary,
    primaryName,
    summary: explainSummary(row, role, {
      keyCount,
      redundantKeyCount,
      uniqueKeyCount,
      fullyRedundant,
    }),
  };
}

/** The one-line justification, keyed by the gateway's current draft role. */
function explainSummary(
  row: GatewayRow,
  role: GatewayRole,
  facts: {
    keyCount: number | null;
    redundantKeyCount: number | null;
    uniqueKeyCount: number | null;
    fullyRedundant: boolean;
  }
): string {
  const what = providesSummary(row);
  switch (role) {
    case 'primary_io':
      return `Picked as your main read/write connection — it provides ${what}.`;
    case 'provides':
      if (facts.uniqueKeyCount !== null && facts.uniqueKeyCount > 0 && facts.redundantKeyCount) {
        return `Kept on: it's the only source for ${facts.uniqueKeyCount} ${plural(
          facts.uniqueKeyCount,
          'reading',
          'readings'
        )} no other gateway has.`;
      }
      return `Kept on — it provides ${what}.`;
    case 'redundant':
      return facts.fullyRedundant
        ? 'Every reading it sees is already covered by another gateway, so we keep it off to reduce load.'
        : "Its data is already covered elsewhere, so we'll keep it off to reduce load.";
    case 'standby':
      return facts.fullyRedundant
        ? 'A full duplicate — kept on standby as a hot backup in case the primary drops.'
        : 'Kept on standby as a backup in case the primary drops.';
    case 'disabled':
      return 'Turned off — it adds no data the rest of your network is missing.';
    default:
      return `It provides ${what}.`;
  }
}

/** Singular/plural helper for the count copy. */
function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * The live plain-English sentence for a card. Prefers the LLM's per-source
 * sentence when the draft role still matches the LLM's role (so an edit
 * regenerates from the deterministic template instead of showing stale copy).
 */
export function formatGatewaySentence(
  rec: SourceFeedRecommendation,
  row: GatewayRow,
  draft: RecommendationDraft
): string {
  const role = draftRoleFor(draft, row);
  const llmEntry = rec.llm?.perSource.find((e) => e.sourceId === row.gatewayId);
  if (llmEntry && llmEntry.role === role && llmEntry.sentence.trim().length > 0) {
    return llmEntry.sentence;
  }
  const name = row.name && row.name.length > 0 ? humanizeGatewayName(row.name) : 'This gateway';
  const what = providesSummary(row);
  switch (role) {
    case 'primary_io':
      return `The ${name} will provide ${what}, and be your main read/write connection.`;
    case 'provides':
      return `The ${name} will provide ${what}.`;
    case 'redundant':
      return `The ${name} sees the same data — we'll keep it off to reduce load.`;
    case 'standby':
      return `The ${name} sees the same data — we'll keep it on standby as a backup.`;
    case 'disabled':
      return `The ${name} will be turned off.`;
    default:
      return `The ${name} will provide ${what}.`;
  }
}

/** Headline + sub copy keyed by the recommendation shape. */
export function recommendationHeadline(rec: SourceFeedRecommendation): {
  title: string;
  sub: string;
} {
  switch (rec.shape) {
    case 'clean':
      return {
        title: "Here's how we'll set up your data",
        sub: 'Your network looks clean — every gateway has a clear job.',
      };
    case 'single':
      return {
        title: "Here's how we'll set up your data",
        sub: 'You have one gateway — it provides everything.',
      };
    case 'multi':
    default:
      return {
        title: "Here's how we'll set up your data",
        sub: "We found some overlapping gateways — here's how we'll keep your data clean.",
      };
  }
}

export interface DraftValidationIssue {
  code: 'would_disable_all_sources' | 'bus_group_without_primary';
  message: string;
  busGroupId?: string | null;
}

export interface DraftValidation {
  ok: boolean;
  issues: DraftValidationIssue[];
}

/**
 * Whether a role keeps the source feeding data (enabled, not a pure off state).
 * Both `disabled` and `redundant` are OFF states, so neither counts as "online".
 * Exported so the `toggleEnabled` on/off control keys the switch off the SAME
 * predicate the reducer uses.
 */
export function roleIsActive(role: GatewayRole): boolean {
  return role === 'primary_io' || role === 'provides' || role === 'standby';
}

/** Client-side floor of the core apply guards (the confirm gate). */
export function validateDraft(
  rec: SourceFeedRecommendation,
  draft: RecommendationDraft
): DraftValidation {
  const issues: DraftValidationIssue[] = [];
  const writableIds = writableGatewayIds(rec);

  const anyActive = rec.rows.some((row) => roleIsActive(draftRoleFor(draft, row)));
  if (rec.rows.length > 0 && !anyActive) {
    issues.push({
      code: 'would_disable_all_sources',
      message: "You can't turn off every gateway — keep at least one feeding data.",
    });
  }

  const groups = new Map<string, GatewayRow[]>();
  for (const row of rec.rows) {
    if (row.busGroupId == null) continue;
    const list = groups.get(row.busGroupId);
    if (list) list.push(row);
    else groups.set(row.busGroupId, [row]);
  }
  for (const [busGroupId, rows] of groups) {
    const hasWritable = rows.some((r) => isWritableRow(r, writableIds));
    if (!hasWritable) continue;
    const groupActive = rows.some((r) => roleIsActive(draftRoleFor(draft, r)));
    if (!groupActive) continue;
    const primaryCount = rows.filter((r) => draftRoleFor(draft, r) === 'primary_io').length;
    if (primaryCount !== 1) {
      issues.push({
        code: 'bus_group_without_primary',
        message:
          'Pick exactly one main read/write connection for each set of overlapping gateways.',
        busGroupId,
      });
    }
  }

  return { ok: issues.length === 0, issues };
}

/** Whether the draft has diverged from the suggestion (flips the CTA copy). */
export function isEdited(rec: SourceFeedRecommendation, draft: RecommendationDraft): boolean {
  return rec.rows.some((row) => draftRoleFor(draft, row) !== row.suggestedRole);
}

/**
 * Derive the NetworkApplyDecisions the screen emits on confirm.
 * `optedInBridgeCandidateIds` (bridge_ftue_offer) carries the setup-session
 * `candidateId`s of any secured-bridge opt-in offers the operator turned ON in
 * this render (see `securedBridgeOffers` / `NetworkRecommendation`'s opt-in
 * row) — folded onto the result as `createCandidates` (contract 4) ONLY when
 * non-empty, so declining (the default / vast majority of applies) leaves the
 * field absent, byte-for-byte identical to today's payload.
 */
export function buildApplyDecisions(
  rec: SourceFeedRecommendation,
  draft: RecommendationDraft,
  optedInBridgeCandidateIds: readonly string[] = []
): NetworkApplyDecisions {
  const roles = rec.rows.map((row) => ({
    gatewayId: row.gatewayId,
    role: draftRoleFor(draft, row),
  }));
  const primary = roles.find((r) => r.role === 'primary_io');
  const preferredN2kOutputSourceId = primary
    ? primary.gatewayId
    : rec.accept.preferredN2kInOutSourceId;
  const createCandidates =
    optedInBridgeCandidateIds.length > 0 ? [...new Set(optedInBridgeCandidateIds)] : undefined;
  return {
    sessionId: rec.sessionId,
    roles,
    preferredN2kOutputSourceId,
    ...(createCandidates ? { createCandidates } : {}),
  };
}

/**
 * Derive the {@link NetworkUserConstraints} the screen POSTs to
 * `.../source-analysis/reevaluate` when the user taps "Reevaluate". Unlike
 * {@link buildApplyDecisions} (which sends the FULL resolved plan), this sends
 * only the user's EDITS as hard pins and lets the cloud resolver complete them
 * into a valid plan. `roles` carries ONLY the rows whose effective draft role
 * diverged from `suggestedRole` (same "edited" notion as {@link isEdited}) — so
 * un-pinned rows stay free for the resolver to fill gaps instead of manufacturing
 * conflicts. `preferredN2kOutputSourceId` is the gateway now holding draft role
 * `primary_io` when that differs from the recommendation's preferred N2K gateway,
 * else `null` (no preference to pin).
 */
export function buildConstraints(
  rec: SourceFeedRecommendation,
  draft: RecommendationDraft
): NetworkUserConstraints {
  const roles = rec.rows
    .filter((row) => draftRoleFor(draft, row) !== row.suggestedRole)
    .map((row) => ({ sourceId: row.gatewayId, role: draftRoleFor(draft, row) }));
  const draftPrimary = rec.rows.find((row) => draftRoleFor(draft, row) === 'primary_io');
  const draftPrimaryId = draftPrimary ? draftPrimary.gatewayId : null;
  const preferredN2kOutputSourceId =
    draftPrimaryId !== null && draftPrimaryId !== rec.accept.preferredN2kInOutSourceId
      ? draftPrimaryId
      : null;
  return { roles, preferredN2kOutputSourceId };
}

// ----- Primary-gateway reason ("why this is your main connection") ---------

/** The subset of facts the primary reason is built from. */
export interface PrimaryReasonFacts {
  msgRate: number;
  flapCount: number;
  deviceCount: number;
}

/**
 * MIRROR of `@neomaris/protocol`'s `buildPrimaryReason` (the web build does not
 * depend on protocol — see this file's header). Keep byte-identical to
 * `packages/protocol/src/source-analysis.ts`; the parity test
 * (`networkRecommendation.test.ts`) imports the protocol source and asserts the
 * two produce the same output for a table of inputs.
 */
export function buildPrimaryReason(
  facts: PrimaryReasonFacts,
  opts?: { suggestion?: { deviceCount: number } }
): string {
  const bits: string[] = [];
  if (facts.deviceCount > 0) {
    bits.push(`${facts.deviceCount} ${facts.deviceCount === 1 ? 'device' : 'devices'}`);
  }
  if (facts.msgRate > 0) {
    bits.push(`${Math.round(facts.msgRate)} msg/s`);
  }
  bits.push(
    facts.flapCount === 0
      ? 'a stable connection'
      : `${facts.flapCount} ${facts.flapCount === 1 ? 'reconnect' : 'reconnects'}`
  );
  const base =
    bits.length > 1 || facts.deviceCount > 0 || facts.msgRate > 0
      ? `Chosen as your main connection — it sees ${joinReasonBits(bits)}.`
      : 'Chosen as the most reliable read/write gateway for this bus.';
  if (opts?.suggestion && opts.suggestion.deviceCount > facts.deviceCount) {
    return `${base} Our suggestion saw ${opts.suggestion.deviceCount} ${
      opts.suggestion.deviceCount === 1 ? 'device' : 'devices'
    }.`;
  }
  return base;
}

function joinReasonBits(parts: string[]): string {
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * The "why this is your main connection" reason for the CURRENT draft primary,
 * for the anchor line above the primary card. When the draft primary is still
 * the LLM's pick and the analysis is ready, use the LLM's narrated reason;
 * otherwise (override, or degraded/no-LLM) build it deterministically from the
 * chosen gateway's facts, optionally contrasting with the LLM's suggestion. The
 * component decides whether to render this or the "Apply to see…" placeholder
 * while an edit is pending (design D12).
 */
export function primaryReasonExplain(
  rec: SourceFeedRecommendation,
  draft: RecommendationDraft
): string {
  const primary = rec.rows.find((r) => draftRoleFor(draft, r) === 'primary_io');
  if (!primary) return '';
  const llmPick = rec.llm?.preferredN2kGatewaySourceId ?? null;
  if (
    rec.status === 'ready' &&
    rec.llm &&
    llmPick === primary.gatewayId &&
    rec.llm.preferredGatewayReason.trim().length > 0
  ) {
    return rec.llm.preferredGatewayReason;
  }
  const facts = overlapFactsFor(rec, primary.gatewayId);
  if (!facts) {
    return rec.llm?.preferredGatewayReason.trim()
      ? rec.llm.preferredGatewayReason
      : 'Chosen as the most reliable read/write gateway for this bus.';
  }
  const suggestionFacts = llmPick ? overlapFactsFor(rec, llmPick) : undefined;
  return buildPrimaryReason(
    { msgRate: facts.msgRate, flapCount: facts.flapCount, deviceCount: facts.deviceCount },
    suggestionFacts && suggestionFacts.sourceId !== facts.sourceId
      ? { suggestion: { deviceCount: suggestionFacts.deviceCount } }
      : undefined
  );
}

/**
 * The STABLE two-section split for the recommendation screen: partition by
 * write-capability (NOT by current draft role), so picking a new primary never
 * moves a card between sections.
 *  - `candidateRows` — the N2K-writable gateways (the primary picker); they can
 *                      both read and write, so any of them may be the primary.
 *  - `otherRows`     — sources that can't write NMEA 2000 (plain SignalK / 0183
 *                      listeners) and therefore can never be the primary.
 * Preserves `rec.rows` order within each section.
 */
export function partitionRows(rec: SourceFeedRecommendation): {
  candidateRows: GatewayRow[];
  otherRows: GatewayRow[];
} {
  const writableIds = writableGatewayIds(rec);
  const candidateRows: GatewayRow[] = [];
  const otherRows: GatewayRow[] = [];
  for (const row of rec.rows) {
    if (isWritableRow(row, writableIds)) candidateRows.push(row);
    else otherRows.push(row);
  }
  return { candidateRows, otherRows };
}

/**
 * The suggested primary gateway the "Recommended" chip keys off: the first row
 * whose `suggestedRole` is `primary_io`, or null when the recommendation names
 * no primary. Deliberately keyed on `suggestedRole` — NOT `preferredIo`, which
 * the dev fixture sets on every writable source (marking several rows).
 */
export function suggestedPrimaryRow(rec: SourceFeedRecommendation): GatewayRow | null {
  return rec.rows.find((r) => r.suggestedRole === 'primary_io') ?? null;
}
